import { z } from 'zod'

// Same PAN shape used across the app's investor-profile forms (`investor-forms.schema.ts`) — a CAS
// password is most often the investor's own PAN.
const PAN_FORMAT = /^[A-Za-z]{5}\d{4}[A-Za-z]$/

// Mirrors CreatePublicCaseDto's file-related fields (kind/password) exactly — the file itself is
// kept as plain component state, not RHF-bound, matching the established `uploadCaseDocumentSchema`
// precedent (Phase 7, features/cases/schemas/case-forms.schema.ts): the real validation that
// matters (magic-byte sniffing) happens server-side regardless of anything client-side can check.
export const uploadDocumentSchema = z
  .object({
    kind: z.enum(['cas_pdf', 'broker_xlsx']),
    password: z.string().optional(),
  })
  .refine((v) => v.kind === 'cas_pdf' || !v.password, {
    message: 'A password only applies to a CAS statement.',
    path: ['password'],
  })
  // A CAS password is usually the investor's PAN, but some CAS files use a KFintech password
  // instead — so this accepts either shape and only rejects empty/trivial garbage, not a
  // non-PAN string outright.
  .refine(
    (v) =>
      v.kind !== 'cas_pdf' ||
      !v.password ||
      PAN_FORMAT.test(v.password) ||
      v.password.trim().length >= 4,
    {
      message: 'Enter your CAS password — your PAN, or your KFintech password.',
      path: ['password'],
    },
  )
export type UploadDocumentFormValues = z.infer<typeof uploadDocumentSchema>
